import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAIN_PRAYERS = ["subuh", "dzuhur", "ashar", "maghrib", "isya"];
const pad = (n) => String(n).padStart(2, "0");
const toSec = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 3600 + m * 60;
};
const fmtDuration = (total) => `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;

const formatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Makassar",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const getWita = () => {
  const parts = formatter.formatToParts(new Date());
  const read = (type) => Number(parts.find((p) => p.type === type).value);
  const h = read("hour") % 24;
  const m = read("minute");
  const s = read("second");
  return { h, m, s, sec: h * 3600 + m * 60 + s, label: `${pad(h)}:${pad(m)}:${pad(s)}` };
};

export const useWitaClock = () => {
  const [now, setNow] = useState(getWita);
  useEffect(() => {
    const timer = setInterval(() => setNow(getWita()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
};

export const usePrayerTimes = () => {
  const now = useWitaClock();
  const query = useQuery({
    queryKey: ["prayer-times"],
    queryFn: async () => (await axios.get(`${API}/prayer-times`)).data,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const timings = query.data?.timings ?? [];
  const main = timings.filter((t) => MAIN_PRAYERS.includes(t.key));
  let next = main.find((t) => toSec(t.time) > now.sec);
  let remaining = null;
  if (next) remaining = toSec(next.time) - now.sec;
  else if (main.length) {
    next = main[0];
    remaining = toSec(main[0].time) + 86400 - now.sec;
  }
  const current = [...main].reverse().find((t) => toSec(t.time) <= now.sec) ?? main[main.length - 1];

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    timings,
    now,
    next,
    current,
    countdown: remaining == null ? null : fmtDuration(remaining),
    hijri: query.data?.hijri,
    gregorian: query.data?.date_readable,
    source: query.data?.source,
  };
};
